import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { MessageSquare, Smartphone, Check, Copy, Heart } from "lucide-react";
import { toast } from "sonner";

const TEXT_TO_GIVE_NUMBER = "405-252-9976";
const TEXT_TO_GIVE_RAW = "4052529976";

export default function Give() {
  const [info, setInfo] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/giving/info");
        setInfo(data);
      } catch {}
    })();
  }, []);

  const copy = (value, label) => {
    navigator.clipboard.writeText(value);
    toast.success(`${label} copied`);
  };

  return (
    <div data-testid="give-page" className="pt-32 pb-20">
      <div className="ccn-container">
        <div className="ccn-overline mb-4">Generosity</div>
        <h1 className="ccn-h1 mb-4 max-w-3xl">Give in faith. <em className="not-italic text-[#C8664D]">Sow in love.</em></h1>
        <p className="text-[#575A58] max-w-2xl text-lg">
          Your gift sustains every sermon, livestream, missions trip and act of mercy this ministry undertakes.
        </p>

        <div className="mt-16 grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Text2Give */}
          <div className="ccn-card p-8 lg:p-10" data-testid="give-text2give-card">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-11 h-11 rounded-full bg-[#C8664D] text-white flex items-center justify-center">
                <MessageSquare className="w-5 h-5" />
              </div>
              <h2 className="font-heading text-2xl">Text2Give</h2>
            </div>
            <p className="text-sm text-[#575A58] mb-6">
              Text any dollar amount (for example, <span className="font-medium text-[#1E1E1E]">"50"</span> or <span className="font-medium text-[#1E1E1E]">"give 25"</span>) to the number below — quick, secure, and from your phone.
            </p>

            <div className="bg-white rounded-2xl p-6 text-center border border-[#2A2E2C]/8">
              <div className="text-xs tracking-[0.2em] uppercase text-[#575A58] mb-2">Text any amount to</div>
              <div className="font-heading text-3xl sm:text-4xl text-[#1E1E1E] mb-5" data-testid="text2give-number">
                {TEXT_TO_GIVE_NUMBER}
              </div>
              <div className="flex flex-col sm:flex-row gap-2">
                <a
                  href={`sms:+1${TEXT_TO_GIVE_RAW}`}
                  data-testid="text2give-sms-link"
                  className="ccn-btn-primary flex-1 justify-center text-sm py-2.5"
                >
                  <MessageSquare className="w-4 h-4" /> Open Messages
                </a>
                <button
                  onClick={() => copy(TEXT_TO_GIVE_NUMBER, "Number")}
                  data-testid="text2give-copy-btn"
                  className="ccn-btn-ghost flex-1 justify-center text-sm py-2.5"
                >
                  <Copy className="w-4 h-4" /> Copy number
                </button>
              </div>
            </div>

            <ul className="mt-6 space-y-2.5 text-sm text-[#575A58]">
              <li className="flex gap-2"><Check className="w-4 h-4 text-[#738C7A] flex-shrink-0 mt-0.5" /> First-time givers receive a one-time secure link to register their card.</li>
              <li className="flex gap-2"><Check className="w-4 h-4 text-[#738C7A] flex-shrink-0 mt-0.5" /> After that, texting an amount instantly completes your gift.</li>
              <li className="flex gap-2"><Check className="w-4 h-4 text-[#738C7A] flex-shrink-0 mt-0.5" /> Standard messaging rates may apply.</li>
            </ul>
          </div>

          {/* Cash App */}
          <div className="ccn-card p-8 lg:p-10 bg-[#F2EBE5]/60" data-testid="give-cashapp-card">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-11 h-11 rounded-full bg-[#738C7A] text-white flex items-center justify-center">
                <Smartphone className="w-5 h-5" />
              </div>
              <h2 className="font-heading text-2xl">Cash App</h2>
            </div>
            <p className="text-sm text-[#575A58] mb-6">
              Prefer Cash App? Send your gift to our official tag — fast, simple, and direct.
            </p>
            <div className="bg-white rounded-2xl p-6 text-center border border-[#2A2E2C]/8">
              <div className="text-xs tracking-[0.2em] uppercase text-[#575A58] mb-2">Our Cashtag</div>
              <div className="font-heading text-3xl sm:text-4xl text-[#1E1E1E] mb-5" data-testid="cashapp-tag">
                {info?.cashapp_tag || "$SEED101"}
              </div>
              <button
                onClick={() => copy(info?.cashapp_tag || "$SEED101", "Cashtag")}
                data-testid="copy-cashapp-btn"
                className="ccn-btn-ghost w-full justify-center text-sm py-2.5"
              >
                <Copy className="w-4 h-4" /> Copy tag
              </button>
              <img
                src="https://customer-assets.emergentagent.com/job_covenant-ministry/artifacts/u6p6pu8q_QR%20Code%20%24SEED101.svg"
                alt="Cash App QR code for $SEED101"
                data-testid="cashapp-qr"
                className="mx-auto mt-5 rounded-xl w-44 h-44 object-contain bg-white p-2"
              />
            </div>
            <div className="mt-6 text-sm text-[#575A58] leading-relaxed flex gap-2">
              <Check className="w-4 h-4 text-[#738C7A] flex-shrink-0 mt-0.5" />
              All gifts go directly to ministry, missions, and mercy work.
            </div>
          </div>
        </div>

        <div className="mt-14 text-center text-sm text-[#575A58] inline-flex items-center gap-2 mx-auto">
          <Heart className="w-4 h-4 text-[#C8664D]" />
          Thank you for partnering with Christian Covenant Network.
        </div>
      </div>
    </div>
  );
}
